import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Logo } from "@/components/brand/Logo";
import { useTheme } from "@/components/ThemeProvider";
import { ArrowRight, Clock, ShieldCheck, Users, Fingerprint } from "lucide-react";

const FEATURES = [
  {
    icon: Clock,
    title: "One clock, many worksites",
    body: "Workers punch from web, mobile, or a shared tablet kiosk — every entry lands on the same approval queue.",
  },
  {
    icon: ShieldCheck,
    title: "Auth built for operators",
    body: "Bcrypt + JWT + TOTP MFA + magic-link resets. Workspace-scoped roles. Rotating refresh tokens with reuse detection.",
  },
  {
    icon: Users,
    title: "Peoplegraph aware",
    body: "Directory-first design — search, filter, and route timesheets by role, site, or manager.",
  },
  {
    icon: Fingerprint,
    title: "Audit-grade trails",
    body: "Every login, invite, and role change is recorded. Platform admins get a cross-workspace view for compliance.",
  },
];

export default function LandingPage() {
  const { theme } = useTheme();
  const brand = theme?.product_display_name || "Quanta";
  const tagline = theme?.tagline || "Your day, understood.";
  return (
    <div className="min-h-screen bg-background">
      <header className="border-b bg-white/70 backdrop-blur">
        <div className="mx-auto flex max-w-6xl items-center justify-between px-6 py-4">
          <Logo />
          <div className="flex items-center gap-2">
            <Button asChild variant="ghost" data-testid="header-login-btn">
              <Link to="/login">Sign in</Link>
            </Button>
            <Button asChild data-testid="header-signup-btn">
              <Link to="/signup">
                Get started <ArrowRight className="h-4 w-4" />
              </Link>
            </Button>
          </div>
        </div>
      </header>

      <section className="brand-navy-gradient relative overflow-hidden text-white">
        <div className="absolute inset-0 dot-grid opacity-40" />
        <div className="relative mx-auto max-w-6xl px-6 py-24 lg:py-32">
          <div className="max-w-3xl">
            <div className="mb-4 inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/5 px-3 py-1 text-xs uppercase tracking-widest text-white/80">
              <span className="h-1.5 w-1.5 rounded-full bg-secondary" />
              Time-tracking · {brand} v1
            </div>
            <h1 className="font-display text-4xl font-semibold leading-tight tracking-tight sm:text-5xl lg:text-6xl">
              Time intelligence
              <span className="block text-cyan">for modern teams.</span>
            </h1>
            <p className="mt-6 max-w-2xl text-lg text-white/75">
              {brand} plugs into your directory, worksite dimensions, and pay policies. Workers clock in from anywhere,
              approvers review in one queue, and hours flow into Bill, AP, and Payroll — the way the platform expects.
              Time moves forward. So do great teams.
            </p>
            <div className="mt-10 flex flex-wrap items-center gap-3">
              <Button asChild size="lg" className="bg-secondary text-white hover:bg-secondary/90" data-testid="hero-signup-btn">
                <Link to="/signup">Start free <ArrowRight className="h-4 w-4" /></Link>
              </Button>
              <Button asChild size="lg" variant="outline" className="border-white/25 bg-white/5 text-white hover:bg-white/10" data-testid="hero-login-btn">
                <Link to="/login">Sign in</Link>
              </Button>
            </div>
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-6xl px-6 py-20">
        <div className="mb-10 max-w-2xl">
          <div className="text-xs font-medium uppercase tracking-widest text-cyan">Foundations, Phase 1</div>
          <h2 className="mt-2 font-display text-3xl font-semibold text-primary">Built on a trustworthy multi-workspace backbone</h2>
          <p className="mt-3 text-muted-foreground">
            Before we track a single minute, we get identity, workspaces, and directory right. That's what this release ships.
          </p>
        </div>
        <div className="grid gap-6 sm:grid-cols-2">
          {FEATURES.map((f) => (
            <div key={f.title} className="card-elevate rounded-2xl border bg-card p-6" data-testid={`feature-${f.title.split(" ")[0].toLowerCase()}`}>
              <div className="mb-4 inline-flex h-10 w-10 items-center justify-center rounded-xl bg-accent text-secondary">
                <f.icon className="h-5 w-5" />
              </div>
              <div className="font-display text-lg font-semibold text-primary">{f.title}</div>
              <p className="mt-2 text-sm text-muted-foreground">{f.body}</p>
            </div>
          ))}
        </div>
      </section>

      <footer className="border-t bg-white">
        <div className="mx-auto flex max-w-6xl flex-col gap-2 px-6 py-8 text-sm text-muted-foreground sm:flex-row sm:justify-between">
          <div>© {new Date().getFullYear()} {brand}. {tagline}</div>
          <div className="flex gap-4">
            <Link to="/login" className="hover:text-primary">Sign in</Link>
            <Link to="/signup" className="hover:text-primary">Create workspace</Link>
            <Link to="/forgot" className="hover:text-primary">Forgot password</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}
